/**
 * One place a screen asks «do this», and two places it can be done.
 *
 * The screens were written against a store they could mutate. That is the right shape for the
 * visual acceptance demo, where the world is an object in the tab, and the wrong shape for the
 * product, where the world is a database behind an authorized API that answers with its own
 * verdict. Rather than fork the screens, the acts they perform are named once (`V07Commands`) and
 * provided here — the demo supplies the in-memory implementation, the product supplies the one
 * that calls the backend and re-reads what it said.
 *
 * A screen therefore never knows which one it is talking to, and the pixel acceptance compares the
 * same components the product renders.
 */
import { createContext, type ReactElement, type ReactNode, useContext } from 'react';
import type { V07Commands } from './commands';

const CommandsContext = createContext<V07Commands | null>(null);

export function V07CommandsProvider({
  commands,
  children,
}: {
  commands: V07Commands;
  children: ReactNode;
}): ReactElement {
  return <CommandsContext.Provider value={commands}>{children}</CommandsContext.Provider>;
}

/**
 * Throws rather than falling back to a no-op: a button wired to a missing provider would look like
 * it worked, and §11 says there is no decorative button.
 */
export function useV07Commands(): V07Commands {
  const commands = useContext(CommandsContext);
  if (commands === null) throw new Error('useV07Commands must be used inside V07CommandsProvider');
  return commands;
}

/**
 * The same context, for MEASUREMENT only, and never for an act a person asked for.
 *
 * A usage beacon is the one thing that must not turn a missing provider into a thrown render: it
 * is not a button, nobody is waiting for it, and a certificate has to open whether or not the
 * measurement can be reported. Every command a person presses still goes through `useV07Commands`
 * and still throws, because a control wired to nothing would look like it worked.
 */
export function useV07CommandsForMeasurement(): V07Commands | null {
  return useContext(CommandsContext);
}
